import { KeyboardArrowDown, KeyboardArrowRight } from '@mui/icons-material'
import type { ScheduledJobView } from '@numeo/types'
import { StatusBadge } from '../_ds/Primitives'
import { formatDuration, formatRelative, isFailing, OUTCOME_CLASS, OUTCOME_LABEL } from './format'
import { JobRuns } from './JobRuns'

interface JobRowProps {
    job: ScheduledJobView
    now: number
    historyHours: number
    expanded: boolean
    onToggle: () => void
}

function JobStatus({ job }: { job: ScheduledJobView }) {
    if (job.status === 'running')
        return (
            <StatusBadge tone="info" dot>
                Running
            </StatusBadge>
        )
    if (job.status === 'not_started') return <StatusBadge tone="off">Not started</StatusBadge>
    if (job.status === 'stopped') return <StatusBadge tone="neutral">Stopped</StatusBadge>
    if (isFailing(job))
        return (
            <StatusBadge tone="warning" dot>
                Failing{job.consecutiveFailures > 1 ? ` ×${job.consecutiveFailures}` : ''}
            </StatusBadge>
        )
    return (
        <StatusBadge tone="on" dot>
            Waiting
        </StatusBadge>
    )
}

export function JobRow({ job, now, historyHours, expanded, onToggle }: JobRowProps) {
    const { recent } = job
    const next = job.queue?.nextDueAt ?? job.nextRunAt
    return (
        <>
            <tr onClick={onToggle} className="cursor-pointer align-top transition-colors hover:bg-slate-50 dark:hover:bg-slate-800/40">
                <td className="px-3 py-2.5">
                    <div className="flex items-start gap-1.5">
                        {expanded ? (
                            <KeyboardArrowDown sx={{ fontSize: 18 }} className="text-slate-400" />
                        ) : (
                            <KeyboardArrowRight sx={{ fontSize: 18 }} className="text-slate-400" />
                        )}
                        <div className="min-w-0">
                            <p className="font-mono text-[13px] font-medium text-slate-900 dark:text-slate-100">{job.name}</p>
                            <p className="text-xs text-slate-500">{job.purpose}</p>
                        </div>
                    </div>
                </td>
                <td className="px-3 py-2.5 text-xs text-slate-600 dark:text-slate-300">
                    {job.schedule ?? '—'}
                    {job.queue && <p className="text-slate-500">{job.queue.pending.toLocaleString()} pending</p>}
                </td>
                <td className="px-3 py-2.5">
                    <JobStatus job={job} />
                </td>
                <td className="px-3 py-2.5 text-xs">
                    {job.lastRun ? (
                        <>
                            <span className={`font-medium ${OUTCOME_CLASS[job.lastRun.outcome]}`}>{OUTCOME_LABEL[job.lastRun.outcome]}</span>
                            <span className="text-slate-500">
                                {' '}
                                · {formatRelative(job.lastRun.finishedAt, now)} · {formatDuration(job.lastRun.durationMs)}
                            </span>
                            {job.lastRun.error && <p className="mt-0.5 line-clamp-2 text-rose-600 dark:text-rose-400">{job.lastRun.error}</p>}
                        </>
                    ) : (
                        <span className="text-slate-400">—</span>
                    )}
                </td>
                <td className="whitespace-nowrap px-3 py-2.5 text-xs text-slate-600 dark:text-slate-300">
                    {job.runningSince ? `running ${formatRelative(job.runningSince, now).replace(' ago', '')}` : next ? formatRelative(next, now) : '—'}
                </td>
                <td className="whitespace-nowrap px-3 py-2.5 text-xs tabular-nums">
                    <span className="text-emerald-700 dark:text-emerald-400" title="Succeeded">
                        {recent.succeeded}
                    </span>
                    <span className="text-slate-300"> / </span>
                    <span className={recent.failed ? 'font-semibold text-rose-700 dark:text-rose-400' : 'text-slate-400'} title="Failed">
                        {recent.failed}
                    </span>
                    <span className="text-slate-300"> / </span>
                    <span className={recent.timed_out ? 'font-semibold text-amber-700 dark:text-amber-400' : 'text-slate-400'} title="Timed out">
                        {recent.timed_out}
                    </span>
                    <span className="text-slate-300"> / </span>
                    <span className="text-slate-400" title="Skipped">
                        {recent.skipped}
                    </span>
                </td>
            </tr>
            {expanded && (
                <tr>
                    <td colSpan={6} className="p-0">
                        <JobRuns job={job} historyHours={historyHours} />
                    </td>
                </tr>
            )}
        </>
    )
}
